import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../context/authContext";
import { loginService } from "../../services/authService";
import { getUserMetadata } from "../../services/userMetadataService";
import { vizybilityConfig } from "../../api/url";
import ApprovalPendingModal from "../../components/auth/ApprovalPendingModal";
import Logo from "../../assets/Images/logo.svg";

const ALLOWED_USER_TYPE = "EcommUser";

const EyeIcon = ({ open }) =>
  open ? (
    <svg
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
      />
    </svg>
  ) : (
    <svg
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );

const Spinner = () => (
  <svg
    style={{ width: 16, height: 16, animation: "spin 1s linear infinite" }}
    fill="none"
    viewBox="0 0 24 24"
  >
    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    <circle
      style={{ opacity: 0.25 }}
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      style={{ opacity: 0.75 }}
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    />
  </svg>
);

const inp = {
  width: "100%",
  border: "2px solid #f3f4f6",
  borderRadius: 10,
  padding: "10px 14px",
  fontSize: 13,
  color: "#111827",
  background: "#f9fafb",
  outline: "none",
  boxSizing: "border-box",
  fontFamily: "inherit",
};

/**
 * isModal  – when true renders only the card (no full-page background)
 * onClose  – called after successful login (modal mode)
 * onSwitch – called with "register" or "forgot-password" to change modal view
 */
const Login = ({ isModal = false, onClose, onSwitch, modalEmail, modalPassword }) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Prefill credentials handed over from the Register flow. Modal mode passes
  // them as props (via authModalContext); route mode passes them through
  // location.state. In-memory only — not persisted.
  const prefillEmail    = modalEmail    || location.state?.email    || "";
  const prefillPassword = modalPassword || location.state?.password || "";
  const [form, setForm] = useState({ email: prefillEmail, password: prefillPassword });
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);

  const handleChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
    setFieldErrors((p) => ({ ...p, [e.target.name]: "" }));
    setError("");
  };

  const validate = () => {
    const errs = { email: "", password: "" };
    if (!form.email.trim()) errs.email = "Email address is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Enter a valid email address.";
    if (!form.password) errs.password = "Password is required.";
    setFieldErrors(errs);
    return !errs.email && !errs.password;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setError("");
    try {
      const data = await loginService(form.email.trim(), form.password, rememberMe);

      if (data?.requiresMfa) {
        setError("Multi-factor authentication is required. Please contact support.");
        return;
      }

      // Backend currently issues a token for any valid Vizybility account
      // regardless of the appType we send. Block non-website users here so
      // SuperAdmin / CRMUser / etc. cannot reach the Paving Market portal.
      let userType = null;
      try { userType = jwtDecode(data.token)?.UserType ?? null; } catch {}
      if (userType !== ALLOWED_USER_TYPE) {
        setError("This account is not permitted to sign in here.");
        return;
      }

      const result = login(data.token, data.refreshToken, rememberMe);
      if (!result.success) throw new Error(result.error);

      // Fetch and cache user metadata so all admin pages can read companyId/contactId
      let pendingApproval = false;
      let hasCompanyAndContact = false;
      try {
        const header = { authorization: `Bearer ${data.token}` };
        const metaRes = await getUserMetadata(header);
        const meta = metaRes?.data?.data ?? metaRes?.data;
        let parsed = {};
        if (meta?.additionalMetadata) {
          try { parsed = JSON.parse(meta.additionalMetadata); } catch {}
        }
        let companyId = meta?.companyId || meta?.CompanyId || parsed?.companyId;
        let contactId = meta?.contactId || meta?.ContactId || parsed?.contactId;
        // additionalMetadata may store the approval state under either
        // "userStatus" (written by Stepper.jsx) or "status" (written by the
        // admin-side approval flow). Accept both so the Pending check fires
        // regardless of which path created the record.
        let userStatus =
          meta?.userStatus || meta?.UserStatus ||
          meta?.status || meta?.Status ||
          parsed?.userStatus || parsed?.status;
        // Prefer per-user generic credentials if the backend issued them in
        // additionalMetadata; otherwise fall back to the shared service
        // account defined in vizybilityConfig. Some records (e.g. users
        // approved via the admin panel) omit these fields, so without the
        // fallback the generic-token swap is skipped and every DMS call 401s.
        const genericEmail =
          parsed?.genericUsername || vizybilityConfig.GenericWebUsername;
        const genericPassword =
          parsed?.genericPassword || vizybilityConfig.GenericWebUserPassword;

        // Store a generic-account token under the DMS key ("vizy-bility-token")
        // so all downstream DMS (DealerWebService) calls run under the shared
        // service identity. Identity/Auth APIs keep using the user's own token
        // (persisted under "vizy-identity-token" by authContext.login above).
        //
        // IMPORTANT: do NOT merge any fields from the generic-user's metadata
        // into companyId / contactId / userStatus. The generic account is a
        // shared service identity that has its own company/contact/"Pending"
        // status baked in — letting those overwrite the real user's (possibly
        // empty) values would mis-route a brand-new registrant to the
        // Approval-Pending modal instead of the company/contact Stepper.
        if (genericEmail && genericPassword) {
          const generic = await loginService(genericEmail, genericPassword, true, { skipRoleCheck: true, appType: "Admin" });
          if (generic?.token) {
            localStorage.setItem("vizy-bility-token", generic.token);
            if (generic.refreshToken) {
              localStorage.setItem("vizy-refresh-token", generic.refreshToken);
            }
          }
        }

        hasCompanyAndContact = Boolean(companyId && contactId);

        if (companyId) {
          localStorage.setItem("company_id", String(companyId));
          localStorage.setItem("getAzureUserData", JSON.stringify({
            CompanyId: companyId,
            ContactId: contactId || null,
            CompanyName: parsed?.companyName || "",
            givenName: result.user?.firstName || "",
            surname: result.user?.lastName || "",
            Phone: parsed?.phone || "",
            // Cached so the header can decide whether the user is "fully logged in"
            // (onboarded + approved) without refetching metadata on every render.
            UserStatus: userStatus || null,
            identities: [{ signInType: "emailAddress", issuerAssignedId: result.user?.email }],
          }));
        }

        // Pending status only blocks users who've already completed the
        // company/contact step. Users without it must be routed to /admin
        // (Stepper) so they can fill it in before approval applies.
        if (hasCompanyAndContact && userStatus === "Pending") {
          pendingApproval = true;
        }
      } catch {} // non-fatal — pages degrade gracefully if metadata unavailable

      if (pendingApproval) {
        setShowPendingModal(true);
        return;
      }

      const roles = Array.isArray(result.user?.role)
        ? result.user.role
        : result.user?.role ? [result.user.role] : [];

      if (isModal && onClose) onClose();
      // Admins always go to the dashboard. WebSiteUsers go to the dashboard
      // only once their company/contact onboarding is complete; otherwise to
      // /admin to fill in the Stepper form.
      if (roles.includes("Admin") || hasCompanyAndContact) {
        navigate("/admin/dashboard");
      } else {
        navigate("/admin");
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  // "Sign up free" / "Sign up" — navigate or switch modal view
  const toRegister = isModal && onSwitch
    ? (e) => { e.preventDefault(); onSwitch("register"); }
    : undefined;

  // "Forgot?" — navigate or switch modal view
  const toForgot = isModal && onSwitch
    ? (e) => { e.preventDefault(); onSwitch("forgot-password"); }
    : undefined;

  const card = (
    <div
      style={{
        position: isModal ? undefined : "relative",
        zIndex: isModal ? undefined : 10,
        width: "100%",
        maxWidth: isModal ? undefined : 800,
        borderRadius: 24,
        overflow: "hidden",
        display: "flex",
        boxShadow: isModal ? undefined : "0 40px 120px rgba(0,0,0,0.65)",
        height: 520,
      }}
    >
      {/* Left dark panel */}
      <div
        style={{
          width: 280,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: 32,
          position: "relative",
          overflow: "hidden",
          background:
            "linear-gradient(160deg,#1a1710 0%,#201d14 55%,#2e2718 100%)",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: 200,
            height: 200,
            borderRadius: "50%",
            background: "#FEBA06",
            opacity: 0.1,
            top: -55,
            right: -55,
            filter: "blur(40px)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 140,
            height: 140,
            borderRadius: "50%",
            background: "#FEBA06",
            opacity: 0.06,
            bottom: -35,
            left: -35,
            filter: "blur(32px)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            width: 120,
            height: 120,
            borderRadius: "50%",
            border: "1px solid rgba(255,255,255,0.06)",
            top: "30%",
            right: -22,
            pointerEvents: "none",
          }}
        />

        <div style={{ position: "relative", zIndex: 1 }}>
          <img
            src={Logo}
            alt="Paving Market"
            onClick={() => navigate("/")}
            style={{
              height: 30,
              width: "auto",
              cursor: "pointer",
              opacity: 0.9,
            }}
          />
        </div>

        <div style={{ position: "relative", zIndex: 1 }}>
          <div
            style={{
              width: 28,
              height: 3,
              background: "#FEBA06",
              borderRadius: 99,
              marginBottom: 18,
            }}
          />
          <div
            style={{
              fontSize: 30,
              fontWeight: 800,
              color: "#ffffff",
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
            }}
          >
            Welcome
            <br />
            Back
          </div>
          <p
            style={{
              fontSize: 12,
              color: "#9ca3af",
              lineHeight: 1.6,
              marginTop: 10,
              maxWidth: 200,
            }}
          >
            Sign in to manage listings, track watchlists, and connect with
            buyers.
          </p>
          <div style={{ marginTop: 24 }}>
            <p style={{ fontSize: 11, color: "#4b5563", marginBottom: 10 }}>
              No account yet?
            </p>
            <Link
              to="/register"
              onClick={toRegister}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(255,255,255,0.2)",
                color: "#ffffff",
                fontSize: 12,
                fontWeight: 600,
                padding: "9px 18px",
                borderRadius: 10,
                textDecoration: "none",
              }}
            >
              Sign up free
              <svg
                width="13"
                height="13"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
          </div>
        </div>

        <p
          style={{
            position: "relative",
            zIndex: 1,
            fontSize: 10,
            color: "#374151",
          }}
        >
          © {new Date().getFullYear()} Paving Market
        </p>
      </div>

      {/* Right white panel */}
      <div
        style={{
          flex: 1,
          background: "#ffffff",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Top bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 28px",
            borderBottom: "1px solid #f3f4f6",
            flexShrink: 0,
          }}
        >
          {!isModal && (
            <button
              onClick={() => navigate("/")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                color: "#9ca3af",
                fontWeight: 500,
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              <svg
                width="13"
                height="13"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Home Page
            </button>
          )}
          <span
            style={{
              fontSize: 12,
              color: "#9ca3af",
              fontWeight: 500,
              marginLeft: "auto",
            }}
          >
            Need an account?{" "}
            <Link
              to="/register"
              onClick={toRegister}
              style={{
                fontWeight: 700,
                color: "#FEBA06",
                textDecoration: "none",
              }}
            >
              Sign up
            </Link>
          </span>
        </div>

        {/* Form area — vertically centered */}
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px 28px",
          }}
        >
          <div style={{ width: "100%", maxWidth: 320 }}>
            <div style={{ marginBottom: 24 }}>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 800,
                  color: "#111827",
                  letterSpacing: "-0.02em",
                  lineHeight: 1.2,
                }}
              >
                Sign in
              </div>
            </div>

            {error && (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 10,
                  padding: "10px 14px",
                  marginBottom: 16,
                  fontSize: 13,
                  color: "#dc2626",
                }}
              >
                <svg
                  style={{
                    width: 15,
                    height: 15,
                    marginTop: 1,
                    flexShrink: 0,
                  }}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {error}
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              style={{ display: "flex", flexDirection: "column", gap: 14 }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#111827",
                    marginBottom: 5,
                  }}
                >
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                  placeholder="you@example.com"
                  style={inp}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#FEBA06";
                    e.target.style.background = "#fff";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#f3f4f6";
                    e.target.style.background = "#f9fafb";
                  }}
                />
              </div>

              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 5,
                  }}
                >
                  <label
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#111827",
                    }}
                  >
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    onClick={toForgot}
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#FEBA06",
                      textDecoration: "none",
                    }}
                  >
                    Forgot?
                  </Link>
                </div>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPwd ? "text" : "password"}
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    required
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    style={{ ...inp, paddingRight: 40 }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#FEBA06";
                      e.target.style.background = "#fff";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#f3f4f6";
                      e.target.style.background = "#f9fafb";
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(!showPwd)}
                    style={{
                      position: "absolute",
                      right: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#d1d5db",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    <EyeIcon open={showPwd} />
                  </button>
                </div>
              </div>

              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={{ width: 15, height: 15, accentColor: "#FEBA06", cursor: "pointer" }}
                />
                <span style={{ fontSize: 13, color: "#6b7280", fontWeight: 500 }}>Keep me signed in</span>
              </label>

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  background: "#FEBA06",
                  color: "#111827",
                  fontWeight: 700,
                  fontSize: 14,
                  padding: "13px 0",
                  borderRadius: 10,
                  border: "none",
                  cursor: "pointer",
                  letterSpacing: "0.03em",
                  boxShadow: "0 6px 20px rgba(254,186,6,0.3)",
                  marginTop: 4,
                  fontFamily: "inherit",
                  opacity: loading ? 0.6 : 1,
                }}
              >
                {loading ? (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                    }}
                  >
                    <Spinner />
                    Signing in…
                  </span>
                ) : (
                  "Sign in"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );

  if (isModal) return (
    <>
    <div style={{ background: "#ffffff", width: "100%" }}>
      {/* Dark header strip — logo is white so needs dark background */}
      <div style={{ background: "#161412", padding: "16px 32px", display: "flex", alignItems: "center"}}>
        <img src={Logo} alt="Paving Market" style={{ height: 36, width: "auto" }} />
      </div>
      {/* Gold accent bar */}
      <div style={{ height: 3, background: "#FEBA06", width: "100%" }} />
      <div style={{ padding: "28px 32px 28px" }}>
      {/* Heading */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: "#161412", letterSpacing: "-0.02em" }}>Sign In</div>
        <p style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>Welcome back. Please enter your credentials.</p>
      </div>
      {error && (
        <div style={{ display:"flex", alignItems:"flex-start", gap:8, background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"9px 12px", marginBottom:16, fontSize:12, color:"#dc2626" }}>
          {error}
        </div>
      )}
      <form onSubmit={handleSubmit} noValidate style={{ display:"flex", flexDirection:"column", gap:16 }}>
        <div>
          <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#161412", letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:7 }}>Email Address</label>
          <input type="email" name="email" value={form.email} onChange={handleChange} autoComplete="email" placeholder="name@company.com"
            style={{ width:"100%", border:`1.5px solid ${fieldErrors.email ? "#dc2626" : "#e5e7eb"}`, borderRadius:8, padding:"11px 14px", fontSize:13, color:"#161412", background: fieldErrors.email ? "#fef2f2" : "#f9fafb", outline:"none", boxSizing:"border-box", fontFamily:"inherit" }}
            onFocus={e=>{ e.target.style.borderColor= fieldErrors.email ? "#dc2626" : "#FEBA06"; e.target.style.background="#fff"; }}
            onBlur={e=>{ e.target.style.borderColor= fieldErrors.email ? "#dc2626" : "#e5e7eb"; e.target.style.background= fieldErrors.email ? "#fef2f2" : "#f9fafb"; }} />
          {fieldErrors.email && <p style={{ margin:"5px 0 0", fontSize:11, color:"#dc2626", display:"flex", alignItems:"center", gap:4 }}>⚠ {fieldErrors.email}</p>}
        </div>
        <div>
          <label style={{ display:"block", fontSize:11, fontWeight:700, color:"#161412", letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:7 }}>Password</label>
          <div style={{ position:"relative" }}>
            <input type={showPwd ? "text" : "password"} name="password" value={form.password} onChange={handleChange} autoComplete="current-password" placeholder="••••••••"
              style={{ width:"100%", border:`1.5px solid ${fieldErrors.password ? "#dc2626" : "#e5e7eb"}`, borderRadius:8, padding:"11px 40px 11px 14px", fontSize:13, color:"#161412", background: fieldErrors.password ? "#fef2f2" : "#f9fafb", outline:"none", boxSizing:"border-box", fontFamily:"inherit" }}
              onFocus={e=>{ e.target.style.borderColor= fieldErrors.password ? "#dc2626" : "#FEBA06"; e.target.style.background="#fff"; }}
              onBlur={e=>{ e.target.style.borderColor= fieldErrors.password ? "#dc2626" : "#e5e7eb"; e.target.style.background= fieldErrors.password ? "#fef2f2" : "#f9fafb"; }} />
            <button type="button" onClick={() => setShowPwd(!showPwd)} style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", color:"#9ca3af", background:"none", border:"none", cursor:"pointer" }}>
              <EyeIcon open={showPwd} />
            </button>
          </div>
          {fieldErrors.password && <p style={{ margin:"5px 0 0", fontSize:11, color:"#dc2626", display:"flex", alignItems:"center", gap:4 }}>⚠ {fieldErrors.password}</p>}
        </div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", userSelect:"none" }}>
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              style={{ width:15, height:15, accentColor:"#FEBA06", cursor:"pointer" }}
            />
            <span style={{ fontSize:13, color:"#6b7280", fontWeight:500 }}>Keep me signed in</span>
          </label>
          <Link to="/forgot-password" onClick={toForgot} style={{ fontSize:12, fontWeight:600, color:"#FEBA06", textDecoration:"none" }}>Forgot Password?</Link>
        </div>
        <button type="submit" disabled={loading} style={{ width:"100%", background:"#FEBA06", color:"#161412", fontWeight:800, fontSize:13, padding:"13px 0", borderRadius:8, border:"none", cursor:"pointer", letterSpacing:"0.06em", textTransform:"uppercase", fontFamily:"inherit", opacity: loading ? 0.7 : 1, marginTop:4 }}>
          {loading ? <span style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}><Spinner />Signing in…</span> : "Sign In"}
        </button>
      </form>
      <p style={{ textAlign:"center", marginTop:22, fontSize:13, color:"#6b7280" }}>
        Don't have an account?{" "}
        <Link to="/register" onClick={toRegister} style={{ color:"#161412", fontWeight:800, textDecoration:"none" }}>Sign Up</Link>
      </p>
      </div>{/* end padding div */}
    </div>
    <ApprovalPendingModal open={showPendingModal} />
    </>
  );

  return (
    <>
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(145deg,#161412 0%,#1f1b14 50%,#2a2319 100%)",
      }}
    >
      {/* Blobs */}
      <div
        style={{
          position: "absolute",
          width: 480,
          height: 480,
          borderRadius: "50%",
          background: "#FEBA06",
          opacity: 0.07,
          top: -160,
          right: -140,
          filter: "blur(85px)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: 360,
          height: 360,
          borderRadius: "50%",
          background: "#FEBA06",
          opacity: 0.05,
          bottom: -110,
          left: -110,
          filter: "blur(70px)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: 260,
          height: 260,
          borderRadius: "50%",
          border: "1px solid rgba(255,255,255,0.04)",
          top: "8%",
          left: "4%",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          width: 140,
          height: 140,
          borderRadius: "50%",
          border: "1px solid rgba(254,186,6,0.07)",
          bottom: "12%",
          right: "6%",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(circle,rgba(255,255,255,0.022) 1px,transparent 1px)",
          backgroundSize: "36px 36px",
          pointerEvents: "none",
        }}
      />
      {card}
    </div>
    <ApprovalPendingModal open={showPendingModal} />
    </>
  );
};

export default Login;
